import React, { FC, ChangeEvent, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { Button, Stack, Typography, Box, LinearProgress } from '@mui/material';

import type { FileEventTarget } from '@shared/types';
import { validateFile } from '@shared/utils/file-validation';
import { MP3Compressor } from '@shared/utils/mp3-compression';
import { audioBufferToWav } from '@shared/utils/audio-utils';

import styles from './music-uploader.module.scss';

interface MusicUploaderProps {
  value: File | string;
  isDisabled?: boolean;
  onChange: (e: File) => void;
  setError: (tr: any) => void;
}

const MusicUploader: FC<MusicUploaderProps> = ({
  value,
  onChange,
  setError,
  isDisabled = false,
}) => {
  const audioPlayerRef = useRef<HTMLAudioElement>(null);
  // removed mobile capture input
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordError, setRecordError] = useState<string>('');
  const [recordSeconds, setRecordSeconds] = useState<number>(0);
  const [isConverting, setIsConverting] = useState(false);
  const [convertProgress, setConvertProgress] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const recordTimerRef = useRef<number | null>(null);
  const { t } = useTranslation();

  // removed Android/iOS-specific capture UI
  const isLocalhost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

  const pickSupportedAudioMime = (): { mimeType: string; ext: string } | null => {
    try {
      if (typeof (window as any).MediaRecorder === 'undefined') return null;
      const candidates: Array<{ mimeType: string; ext: string }> = [
        { mimeType: 'audio/webm;codecs=opus', ext: 'webm' },
        { mimeType: 'audio/webm', ext: 'webm' },
        { mimeType: 'audio/ogg;codecs=opus', ext: 'ogg' },
        { mimeType: 'audio/ogg', ext: 'ogg' },
        // Safari (iOS/macOS) чаще всего поддерживает mp4/m4a
        { mimeType: 'audio/mp4;codecs=mp4a.40.2', ext: 'm4a' },
        { mimeType: 'audio/mp4', ext: 'm4a' },
        { mimeType: 'audio/aac', ext: 'm4a' },
      ];
      for (const c of candidates) {
        if ((window as any).MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c.mimeType)) {
          return c;
        }
      }
      // Если специфичный не найден — пусть браузер решит сам (может отдать webm)
      return { mimeType: '', ext: 'webm' };
    } catch {
      return null;
    }
  };

  // Функция конвертации аудио в MP3
  const convertToMP3 = async (audioFile: File): Promise<File> => {
    try {
      setIsConverting(true);
      setConvertProgress(0);
      
      console.log('Начинаем конвертацию записанного аудио в MP3...');
      
      // Создаем AudioContext для декодирования
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Читаем файл
      const arrayBuffer = await audioFile.arrayBuffer();
      setConvertProgress(10);
      
      // Декодируем аудио
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      setConvertProgress(20);
      
      console.log('Аудио декодировано, длительность:', audioBuffer.duration, 'сек');
      
      // Создаем WAV файл из AudioBuffer
      const wavBlob = await audioBufferToWav(audioBuffer);
      const tempFile = new File([wavBlob], 'temp.wav', { type: 'audio/wav' });
      setConvertProgress(30);
      
      // Конвертируем в MP3
      const compressor = new MP3Compressor();
      const compressionResult = await compressor.compressMP3(tempFile, (progress) => {
        // Прогресс от 30% до 90%
        const encodingProgress = 30 + (progress.progress * 0.6);
        setConvertProgress(encodingProgress);
      });
      
      setConvertProgress(100);
      
      // Создаем финальный файл с правильным именем
      const originalName = audioFile.name.replace(/\.[^/.]+$/, '');
      const mp3File = new File([compressionResult.compressedFile], `${originalName}.mp3`, { 
        type: 'audio/mpeg' 
      });
      
      console.log('Конвертация завершена, размер MP3:', mp3File.size, 'байт');
      
      audioContext.close();
      return mp3File;
      
    } catch (error) {
      console.error('Ошибка конвертации в MP3:', error);
      throw error;
    } finally {
      setIsConverting(false);
      setConvertProgress(0);
    }
  };


  useEffect(() => {
    if (!value) {
      return;
    }

    if (typeof value === 'string') {
      return setAudioUrl(value);
    }

    return setAudioUrl(URL.createObjectURL(value));
  }, [value]);

  useEffect(() => {
    if (audioUrl && audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.load();
    }
  }, [audioUrl]);

  const handleMusicInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as FileEventTarget;

    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      const validation = validateFile(file, 'audio');
      
      if (validation.isValid) {
        // Проверяем, нужно ли конвертировать файл в MP3
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const needsConversion = fileExtension && !['mp3', 'mpeg'].includes(fileExtension);
        
        if (needsConversion) {
          try {
            console.log('Загруженный файл не в формате MP3, конвертируем...');
            const mp3File = await convertToMP3(file);
            onChange(mp3File);
            setError('');
          } catch (conversionError) {
            console.error('Ошибка конвертации загруженного файла:', conversionError);
            // В случае ошибки конвертации используем оригинальный файл
            onChange(file);
            setError('');
          }
        } else {
          onChange(file);
          setError('');
        }
      } else {
        setError(validation.error || 'Unknown validation error');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isDisabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (isDisabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validation = validateFile(file, 'audio');
      
      if (validation.isValid) {
        // Проверяем, нужно ли конвертировать файл в MP3
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const needsConversion = fileExtension && !['mp3', 'mpeg'].includes(fileExtension);
        
        if (needsConversion) {
          try {
            console.log('Перетащенный файл не в формате MP3, конвертируем...');
            const mp3File = await convertToMP3(file);
            onChange(mp3File);
            setError('');
          } catch (conversionError) {
            console.error('Ошибка конвертации перетащенного файла:', conversionError);
            // В случае ошибки конвертации используем оригинальный файл
            onChange(file);
            setError('');
          }
        } else {
          onChange(file);
          setError('');
        }
      } else {
        setError(validation.error || 'Unknown validation error');
      }
    }
  };

  const startTimer = () => {
    if (recordTimerRef.current) window.clearInterval(recordTimerRef.current);
    setRecordSeconds(0);
    recordTimerRef.current = window.setInterval(() => {
      setRecordSeconds((s) => s + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (recordTimerRef.current) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  };

  const handleStartRecording = async () => {
    if (isDisabled) return;
    setRecordError('');
    try {
      if (!isLocalhost && window.location.protocol !== 'https:') {
        setRecordError('Запись доступна только по HTTPS (или localhost). Откройте страницу по защищенному протоколу.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const picked = pickSupportedAudioMime();
      const options = picked && picked.mimeType ? { mimeType: picked.mimeType } : undefined;
      const recorder = new MediaRecorder(stream, options as MediaRecorderOptions | undefined);
      recordedChunksRef.current = [];
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = async () => {
        stopTimer();
        const recMime = recorder.mimeType || (picked?.mimeType || 'audio/webm');
        const extension = picked?.ext || (recMime.includes('ogg') ? 'ogg' : recMime.includes('mp4') ? 'm4a' : 'webm');
        const blob = new Blob(recordedChunksRef.current, { type: recMime });
        const originalFile = new File([blob], `recording.${extension}`);
        
        try {
          // Автоматически конвертируем в MP3 для лучшей совместимости
          console.log('Запись завершена, начинаем конвертацию в MP3...');
          const mp3File = await convertToMP3(originalFile);
          
          // Используем конвертированный MP3 файл
          onChange(mp3File);
          setError('');
          setAudioUrl(URL.createObjectURL(mp3File));
          
          console.log('Аудио успешно записано и конвертировано в MP3');
        } catch (conversionError) {
          console.error('Ошибка конвертации в MP3, используем оригинальный файл:', conversionError);
          // В случае ошибки конвертации используем оригинальный файл
          onChange(originalFile);
          setError('');
          setAudioUrl(URL.createObjectURL(originalFile));
        }
        
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      startTimer();
    } catch (err) {
      // Детализируем причину
      const message = (err as any)?.name === 'NotAllowedError'
        ? 'Доступ к микрофону запрещён. Разрешите доступ в настройках браузера.'
        : (err as any)?.name === 'NotFoundError'
          ? 'Микрофон не найден. Подключите устройство ввода звука.'
          : 'Не удалось получить доступ к микрофону. Проверьте HTTPS и разрешения.';
      setRecordError(message);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // removed mobile capture handler

  const getMusicFileName = (musicFile: File | string | undefined) => {
    if (!musicFile) {
      return t('clientAll.uploadMusic');
    }

    if (typeof musicFile === 'string') {
      return musicFile.split('/').pop() || musicFile;
    }

    return musicFile.name;
  };

  return (
    <Stack
      className={`${styles.musicUploaderWrapper} ${isDragOver ? styles.dragOver : ''}`}
      sx={{ 
        alignItems: 'center',
        border: '2px dashed #ccc',
        borderRadius: 2,
        padding: 2,
        minHeight: 120,
        transition: 'all 0.2s ease',
        backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.08)' : '#fafafa',
        borderColor: isDragOver ? '#1976d2' : '#ccc',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        '&:hover': {
          borderColor: isDisabled ? '#ccc' : '#1976d2',
          backgroundColor: isDisabled ? '#fafafa' : 'rgba(25, 118, 210, 0.04)'
        }
      }}
      spacing={2}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {!value && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: 'center', mb: 1 }}
        >
          {t('clientAll.dragDropOrPasteMusic')}
        </Typography>
      )}
      
      <Button
        variant="contained"
        disabled={isDisabled}
        size="small"
        component="label"
        className={styles.uploadButton}
      >
        {getMusicFileName(value)}
        <input
          accept="audio/*,.mp3,.wav,.ogg"
          type="file"
          hidden
          onChange={handleMusicInputChange}
        />
      </Button>

      {!isDisabled && (
        <Stack direction="row" spacing={1} alignItems="center">
          {!isRecording ? (
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleStartRecording}
              startIcon={<MicIcon />}
            >
              {t('clientAll.recordMic')}
            </Button>
          ) : (
            <Button 
              variant="outlined" 
              color="error" 
              size="small" 
              onClick={handleStopRecording}
              startIcon={<StopIcon />}
            >
              {t('clientAll.stop')}
            </Button>
          )}
          <Typography variant="caption" color="textSecondary">
            {isRecording ? t('clientAll.recordingInProgress', { m: Math.floor(recordSeconds / 60), s: String(recordSeconds % 60).padStart(2, '0') }) : ''}
          </Typography>
        </Stack>
      )}

      {/* mobile capture UI removed as requested */}
      
      {recordError && (
        <Typography variant="caption" color="error">
          {recordError}
        </Typography>
      )}

      {/* Прогресс конвертации в MP3 */}
      {isConverting && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1 }}>
            Конвертация в MP3... {Math.round(convertProgress)}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={convertProgress} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#1976d2'
              }
            }} 
          />
        </Box>
      )}
    </Stack>
  );
};

export default MusicUploader;
